import React, {useEffect, useState} from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stepper, Step, StepLabel, Box, Typography, TextField, MenuItem, Select, IconButton ,CircularProgress} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from "../axiosConfig";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';


const steps = ['Review Cart', 'Pickup Info', 'Confirm Order', 'Order Confirmation'];

const CartDialog = ({ open, onClose, cart, totalPrice ,setCart }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [pickupLocation, setPickupLocation] = useState('');
    const [name, setName] = useState('');
    const [pickUpDate, setPickUpDate] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [orderId, setOrderId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);

    useEffect(() => {
        const getNextBusinessDay = () => {
            let date = new Date();
            let day = date.getDay();

            if (day === 5) { // Friday → Next Tuesday
                date.setDate(date.getDate() + 4);
            } else if (day === 6) { // Saturday → Next Tuesday
                date.setDate(date.getDate() + 3);
            } else if (day === 0) { // Sunday → Next Tuesday
                date.setDate(date.getDate() + 2);
            } else {
                // If it's between Monday to Thursday, find the next valid business day
                date.setDate(date.getDate() + 1);
                while (date.getDay() < 2 || date.getDay() > 5) {
                    date.setDate(date.getDate() + 1);
                }
            }

            // Format date as "Tue, Mar 28, 2025"
            const formattedDate = date.toDateString();
            setPickUpDate(formattedDate);
        };

        getNextBusinessDay();
    }, []);


    const handleNext = async () => {


        if (activeStep === 1) {
            validateEmail(email);
            validatePhone(phone);
            setActiveStep((prev) => prev + 1);
        }else if (activeStep === steps.length - 2) {
            await handlePreOrder();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    // Validate Email
    const validateEmail = (value) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setEmailError(!regex.test(value));
    };

    // Validate Phone
    const validatePhone = (value) => {
        const regex = /^\d{3}\d{3}\d{4}$/; // Format: 123-456-7890
        setPhoneError(!regex.test(value));
    };


    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handlePreOrder = async () => {
        setLoading(true);
        const orderDetails = {
            customer: { name, email, phone, pickupLocation,pickUpDate },
            cart: Object.entries(cart).map(([item, details]) => ({
                title: item,
                quantity: details.quantity,
                price: details.price,
                contents: details.contents,
                id: details.id,
            })),
            totalPrice,
        };

        try {
            const response = await axiosInstance.post(`/preorderbox/customer`, orderDetails);
            setOrderId(response.data); // Capture order ID from backend
            setActiveStep((prev) => prev + 1); // Go to confirmation step

            clearCart(); // ✅ Clear cart after successful order

        } catch (error) {
            console.error('Failed to place order:', error);
            alert('Failed to place order. Please try again.');

        }
        setLoading(false);
    };

    // ✅ Clear the cart
    const clearCart = () => {
        Object.keys(cart).forEach((key) => delete cart[key]);
    };

    // ✅ Reset state on close
    const handleClose = () => {
        setActiveStep(0);
        setPickupLocation('');
        setName('');
        setEmail('');
        setPhone('');
        setOrderId(null);
        onClose();
    };



    const handleAddItem = (item) => {
        setCart((prev) => ({
            ...prev,
            [item]: { ...prev[item], quantity: prev[item].quantity + 1 }
        }));
    };

    const handleRemoveItem = (item) => {
        if (cart[item].quantity > 1) {
            setCart((prev) => ({
                ...prev,
                [item]: { ...prev[item], quantity: prev[item].quantity - 1 }
            }));
        }
    };

    const handleDeleteItem = (item) => {
        setCart((prev) => {
            const newCart = { ...prev };
            delete newCart[item];
            return newCart;
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            {/* Header */}
            <DialogTitle>
                Complete Your Order
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Stepper */}
            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Step Content */}
                <Box mt={3}>
                    {/* Step 1 - Review Cart */}
                    {activeStep === 0 && (
                        <Box>

                            <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                                {Object.entries(cart).map(([item, details]) => (
                                    <Box key={item} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 1, borderBottom: '1px solid #ddd' }}>
                                        <Typography>
                                            {details.quantity} x {item}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography>
                                                ${(details.quantity * parseFloat(details.price)).toFixed(2)}
                                            </Typography>
                                            <IconButton onClick={() => handleAddItem(item)} sx={{
                                                backgroundColor: '#f0f0f0',
                                                '&:hover': {
                                                    backgroundColor: '#e0e0e0'
                                                },
                                                borderRadius: '8px',
                                                padding: '4px'
                                            }}>
                                                <AddIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleRemoveItem(item)} sx={{
                                                backgroundColor: '#f0f0f0',
                                                '&:hover': {
                                                    backgroundColor: '#e0e0e0'
                                                },
                                                borderRadius: '8px',
                                                padding: '4px'
                                            }}>
                                                <RemoveIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDeleteItem(item)} sx={{
                                                backgroundColor: '#ffebee',
                                                '&:hover': {
                                                    backgroundColor: '#ffcdd2'
                                                },
                                                borderRadius: '8px',
                                                padding: '4px'
                                            }}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            <Box mt={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6">Total Price:</Typography>
                                <Typography variant="h6">${totalPrice.toFixed(2)}</Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Step 2 - Pickup and Contact Info */}
                    {activeStep === 1 && (
                        <Box>

                            <Select
                                value={pickupLocation}
                                onChange={(e) => setPickupLocation(e.target.value)}
                                displayEmpty
                                fullWidth
                                required
                                sx={{ marginBottom: 2 }}
                            >
                                <MenuItem value="">Select Pickup Location</MenuItem>
                                <MenuItem value="2436 Flower Petal Rd, Frisco Tx 75033">Holly Hock, Frisco, Tx</MenuItem>
                                <MenuItem value="800 W Eldorado Pkwy #128, Little Elm, TX 75068">Nag's Kitchen, Little Elm, Tx</MenuItem>
                            </Select>

                            <TextField
                                label="Pick up Date"
                                value={pickUpDate}
                                InputProps={{
                                    readOnly: true,
                                }}
                                onChange={(e) => setPickUpDate(e.target.value)} fullWidth required sx={{ marginBottom: 2 }}
                                variant="outlined"
                            />
                            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required sx={{ marginBottom: 2 }} />
                            <TextField
                                label="Email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    validateEmail(e.target.value);
                                }}
                                onBlur={() => validateEmail(email)}
                                error={emailError}
                                helperText={emailError ? 'Invalid email format' : ''}
                                fullWidth
                                required
                                sx={{
                                    marginBottom: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: emailError ? '#d32f2f' : '#ccc',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: emailError ? '#d32f2f' : '#1976d2',
                                        },
                                    },
                                }}
                                variant="outlined"
                            />

                            {/* Phone */}
                            <TextField
                                label="Phone Number"
                                value={phone}
                                onChange={(e) => {
                                    setPhone(e.target.value);
                                    validatePhone(e.target.value);
                                }}
                                onBlur={() => validatePhone(phone)}
                                error={phoneError}
                                helperText={phoneError ? 'Invalid phone format (123-456-7890)' : ''}
                                fullWidth
                                required
                                sx={{
                                    marginBottom: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: phoneError ? '#d32f2f' : '#ccc',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: phoneError ? '#d32f2f' : '#1976d2',
                                        },
                                    },
                                }}
                                variant="outlined"
                            />


                        </Box>
                    )}

                    {/* Step 3 - Confirm Order */}
                    {activeStep === 2 && (
                        <Box>

                            <Box>
                                <Typography><strong>Name:</strong> {name}</Typography>
                                <Typography><strong>Email:</strong> {email}</Typography>
                                <Typography><strong>Phone:</strong> {phone}</Typography>
                                <Typography><strong>Pickup Location:</strong> {pickupLocation}</Typography>
                                <Typography><strong>Pickup Date:</strong> {pickUpDate}</Typography>
                                <Typography mt={2}><strong>Total Price:</strong> ${totalPrice.toFixed(2)}</Typography>
                            </Box>
                        </Box>
                    )}

                    {/* ✅ Step 4 - Order Confirmation */}
                    {activeStep === 3 && orderId && (
                        <Box>

                            <Typography>
                                Thank you for your order. Your order ID is <strong>#{orderId}</strong>.
                            </Typography>
                            <Typography>
                                We'll notify you when your order is ready for pickup.
                            </Typography>
                        </Box>
                    )}
                    {/* ✅ Show Loading Spinner */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions>
                {activeStep > 0 && activeStep < 3 && (
                    <Button onClick={handleBack} color="secondary">
                        Back
                    </Button>
                )}
                {activeStep < 3 && (
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        color="primary"
                        disabled={
                            (activeStep === 1 && (!pickupLocation || !name || !email || !phone))
                        }
                    >
                        {activeStep === steps.length - 2 ? 'Confirm Order' : 'Next'}
                    </Button>
                )}
                {activeStep === 3 && (
                    <Button onClick={handleClose} variant="contained" color="primary">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CartDialog;
